import {
  DelimitedContent,
  FvLinkButton,
  Icon,
  Slash,
} from '@fv/client-components'
import { displayLocShipType } from '@fv/client-core'
import { formatPhone } from '@fv/models/core'

import { Accessorials } from '@/components/shared/EquipmentAccessorial'
import { StopAccessorials } from '@/components/shared/StopAccessorials'
import { useItemDetailParts } from '@/hooks/useItemDetailParts'
import { useTypeNames } from '@/hooks/useTypeNames'
import type { Opportunity } from '@/types/Opportunity'

type Props = {
  opportunity: Opportunity
}

export const Details = ({ opportunity }: Props) => {
  const {
    billTo,
    equipmentRequested,
    items,
    loadId,
    locations,
    pickup,
    refNums = [],
  } = opportunity

  const { equipmentName } = useTypeNames()
  const getItemDetailParts = useItemDetailParts()

  return (
    <>
      {billTo?.address && (
        <div className="flex items-center  bg-fv-beer-50 flex-wrap border border-fv-gray p-3 mb-4">
          <p className="overflow-hidden">
            <span className="font-semibold">Bill to:</span> {billTo.company}
            <br />
            {billTo.address}
            {billTo.address2 ? ` ${billTo.address2}` : ''} / {billTo.city},{' '}
            {billTo.state} {billTo.postalCode}
            {billTo.contactName || billTo.contactPhone ? <br /> : null}
            {billTo.contactName}
            {billTo.contactName && billTo.contactPhone ? ' / ' : ''}
            {billTo.contactPhone && (
              <>
                <FvLinkButton
                  theme="underlined"
                  href={`tel:${billTo.contactPhone}`}
                >
                  {formatPhone(billTo.contactPhone)}
                </FvLinkButton>
                {billTo.contactPhoneExt ? ` x${billTo.contactPhoneExt}` : ''}
              </>
            )}
            {billTo.contactEmail ? <br /> : null}
            {billTo.contactEmail && (
              <FvLinkButton
                theme="underlined"
                href={`mailto:${billTo.contactEmail}`}
              >
                {billTo.contactEmail}
              </FvLinkButton>
            )}
          </p>
        </div>
      )}
      <div className="flex-1">
        {locations.map(x => {
          const locationItems = items.filter(
            i => i.dropLocationSequence === x.sequence,
          )
          const shipType = displayLocShipType(x)

          const {
            contactEmail: email,
            contactName: name,
            contactPhone: phone,
          } = x

          return (
            <ul
              key={x.sequence}
              className="divide divide-y [&>li]:py-2 divide-gray-300 divide-dotted [&+&]:mt-4"
            >
              <li>
                <div className="border-b border-fv-gray mb-4 pb-1">
                  <Icon
                    icon="map-marker"
                    className={
                      x.type === 'origin'
                        ? 'text-[#99d87a]'
                        : x.type === 'stop'
                          ? 'text-[#d4d4a3]'
                          : 'text-[#ff9a59]'
                    }
                  />
                  <span>{x.company}</span>
                </div>
                <p className="mb-0">
                  {x.address ? `${x.address} / ` : ''}
                  {x.address2?.length ? x.address2 + ' /' : ''} {x.city},{' '}
                  {x.state} {x.postalCode} / {x.country.toUpperCase()}
                  {x.type === 'origin' &&
                    ` / ${equipmentName(equipmentRequested.type)}`}
                  <Accessorials
                    accessorials={equipmentRequested?.accessorials}
                  />
                </p>
              </li>
              {locationItems.length > 0 &&
                locationItems.map((item, index) => (
                  <li className="general-list__item" key={item._id}>
                    <span>
                      <DelimitedContent delimiter={<Slash />}>
                        {getItemDetailParts(item, opportunity.workflow)}
                      </DelimitedContent>
                    </span>
                  </li>
                ))}

              {x.type === 'origin' && (
                <li>
                  <p className="mb-0">
                    {!!refNums?.length && (
                      <span>
                        Shipper's # {refNums.map(n => n.value).join(' / ')} /{' '}
                      </span>
                    )}
                    {pickup?.confirmationNumber && (
                      <span>Pickup # {pickup.confirmationNumber} / </span>
                    )}
                    Pickup hours: {x.opensAt} - {x.closesAt}
                  </p>
                </li>
              )}
              {x.type === 'stop' && (
                <li>
                  <p className="mb-0">
                    Dock hours: {x.opensAt} - {x.closesAt}
                  </p>
                </li>
              )}
              {x.type === 'destination' && (
                <li>
                  <p className="mb-0">
                    {!!x.refNums?.length && (
                      <>
                        <span>
                          Purchase order #{' '}
                          {x.refNums?.map(n => n.value).join(' / ')}
                        </span>
                        <span> / </span>
                      </>
                    )}
                    Delivery hours: {x.opensAt} - {x.closesAt}
                  </p>
                </li>
              )}

              {(name || phone || email) && (
                <li>
                  <p className="mb-0">
                    {name}
                    {name && (phone || email) && ' / '}
                    {phone && (
                      <FvLinkButton theme="underlined" href={`tel:${phone}`}>
                        {formatPhone(phone)}
                      </FvLinkButton>
                    )}
                    {phone && email && ' / '}
                    {email && (
                      <FvLinkButton theme="underlined" href={`mailto:${email}`}>
                        {email}
                      </FvLinkButton>
                    )}
                  </p>
                </li>
              )}

              {(shipType || !!x.accessorials?.length) && (
                <li>
                  <DelimitedContent delimiter={<Slash />}>
                    {shipType}
                    {!!x.accessorials?.length && (
                      <StopAccessorials loadId={loadId} location={x} />
                    )}
                  </DelimitedContent>
                </li>
              )}
              {x.instructions && (
                <li>
                  <p className="mb-0">{x.instructions}</p>
                </li>
              )}
            </ul>
          )
        })}
      </div>
    </>
  )
}
